import { request } from '@/api/axios'
import type { Active, Attribute, Company, Device, Event, PagingParam, PagingRes, Permission, SimAndDevice, SimOverview, SimpleCompanyPermission, SystemRole, SystemRoleDetail, TypeWithPagingParam, UserDetail, AbnormalDevice } from '@/types/global'
export function queryRoleList(params: TypeWithPagingParam<{ roleName?: string }>) {
  return request<PagingRes<Omit<SystemRole, 'resourceIdList'>>>({
    url: '/admin/role',
    method: 'get',
    params,
  })
}

// 获取角色详情
export function getRoleDetail(roleId: string): Promise<SystemRoleDetail> {
  return request<SystemRoleDetail>({
    url: `/admin/role/${roleId}`,
    method: 'get',
  })
}

// 保存角色
export function postRole(data: Omit<SystemRole, 'roleId'>) {
  return request<null>({
    url: '/admin/role',
    method: 'post',
    data,
  })
}

// 更新角色
export function updateRole(data: SystemRole) {
  return request<null>({
    url: '/admin/role',
    method: 'put',
    data,
  })
}

// 删除角色
export function deleteRoleDetail(roleId: string) {
  return request<null>({
    url: `/admin/role/${roleId}`,
    method: 'delete',
  })
}

interface QueryUserListParam extends PagingParam {
  userName?: string,
  phoneNum?: string,
  fullName?: string,
}
// 用户列表
export function queryUserList(params: QueryUserListParam) {
  return request<PagingRes<UserDetail>>({
    url: '/admin/user',
    method: 'get',
    params,
  })
}

// 角色列表名称和id
export function queryRoleNameList() {
  return request({
    url: '/admin/role/name',
    method: 'get',
  })
}

// 保存用户
export function postUser(data: any) {
  return request({
    url: '/admin/user',
    method: 'post',
    data,
  })
}

// 停启用
export function updateUserStatus(data: { userId: string, status: 0 | 1 }) {
  return request<null>({
    url: '/admin/user/status',
    method: 'post',
    data,
  })
}

// 重置密码
export function postUserPassword(data: { userId: string }) {
  return request<{
    userName: string;
    password: string;
  }>({
    url: '/admin/user/password',
    method: 'post',
    data,
  })
}

// 删除用户
export function deleteUserDetail(userId: string) {
  return request<null>({
    url: `/admin/user/${userId}`,
    method: 'delete',
  })
}

// 更新用户
export function updateUser(data: any) {
  return request({
    url: '/admin/user',
    method: 'put',
    data,
  })
}

// 查询用户
export function getUserDetail(userId: string) {
  return request({
    url: `/admin/user/info/${userId}`,
    method: 'get',
  })
}

// 校验手机号或用户名
export function checkAccount(params: any) {
  return request({
    url: '/admin/user/checkUserNameOrPhoneNum',
    method: 'get',
    params,
  })
}
// 获取用户菜单权限列表
export function queryPermissionList() {
  return request<Permission[]>({
    url: '/admin/user/resource/current/user',
    method: 'get',
  })
}

// 获取企业用户权限管理列表
interface QueryCompanyPermissionListParam extends PagingParam {
  keyword?: string,
  startTime?: string,
  endTime?: string,
  time: undefined,
}

export function queryCompanyPermissionList(params: QueryCompanyPermissionListParam) {
  return request<PagingRes<SimpleCompanyPermission>>({
    url: '/admin/web/clientManagement/enterpriseRightManagement/pageCompany',
    method: 'get',
    params,
  })
}

// 冻结企业
export function frozenCompany(companyId: string) {
  return request<null>({
    url: `/admin/company/frozen/${companyId}`,
    method: 'delete',
  })
}

export function defrozenCompany(companyId: string) {
  return request<null>({
    url: `/admin/company/defrozen/${companyId}`,
    method: 'delete',
  })
}

// 获取全部公司
export function getCompany(params: TypeWithPagingParam<{ companyName?: string }>) {
  return request<PagingRes<Company>>({
    url: '/admin/web/clientManagement/enterpriseRightManagement/pageCompany',
    method: 'get',
    params,
  })
}

// 查询产品列表
export function queryProductList(params: any) {
  return request({
    url: '/admin/device/query/page/product',
    method: 'get',
    params,
  })
}

// 设备分页列表
type QueryDeviceListParam = TypeWithPagingParam<{
  ccid?: string;
  companyId?: string;
  deviceName?: string;
  deviceStatus?: number[];
  deviceType?: number;
  productKey?: string;
  factoryCode?: string;
  categoryCode?: string;
  modelCode?: string;
  otaVersion?: string;
}>
type QueryDeviceListResponse = PagingRes<Device> & {
  activeDeviceCount: number,
  onlineDeviceCount: number,
  totalDeviceCount: number,
}
export function queryDeviceList(data: QueryDeviceListParam) {
  return request<QueryDeviceListResponse>({
    url: '/admin/device/query/page/device',
    method: 'post',
    data,
  })
}

// 查询设备详情的基础信息
export function getDeviceBasicInfo(data: { companyId: string, deviceName: string, productKey: string }) {
  return request<Pick<Device, 'companyId' | 'companyName' | 'deviceName' | 'deviceStatus' | 'deviceType' | 'productKey' | 'productName'>>({
    url: '/admin/device/query/device/basic/info',
    method: 'post',
    data,
  })
}

interface GetDevicePropertiesResponse extends Omit<Device, 'ccid'> {
  dataItemList: Attribute[];
  categoryCode: string;
  factoryMode: string;
  iotId: string;
  modelMode: string,
}
// 查询运行状态的数据
export function getDeviceProperties(data: {
  productKey: string,
  deviceName: string,
  companyId: string,
  includeOps: boolean,
  name?: string,
}) {
  return request<GetDevicePropertiesResponse>({
    url: '/admin/device/query/device/properties',
    method: 'post',
    data,
  })
}

interface ExportDevicePropertiesParam {
  deviceName: string;
  productKey: string;
  companyId: string;
  startTime: number,
  endTime: number;
  includeOps: boolean;
}
// 导出实时运维数据
export function exportDeviceProperties(data: ExportDevicePropertiesParam) {
  return request<string>({
    url: '/admin/device/export/device/properties',
    method: 'post',
    data,
  })
}

// 查询历史记录
export function getDeviceHistoryProperties(data: any) {
  return request({
    url: '/admin/device/list/device/properties',
    method: 'post',
    data,
  })
}

// 导出历史运维数据
export function exportDeviceHistoryProperties(data: any) {
  return request({
    url: '/admin/device/export/device/history/properties',
    method: 'post',
    data,
  })
}

export interface GetDeviceServiceData extends PagingParam {
  callType?: number,
  productKey: string;
  deviceName: string;
  search?: string;
}
// 获取动作调用列表
export function getDeviceServiceData(data: GetDeviceServiceData) {
  return request<PagingRes<Active>>({
    url: '/admin/device/query/device/action/page',
    method: 'post',
    data,
  })
}
export interface GetEventQueryParam extends PagingParam {
  type: number;
  productKey: string;
  deviceName: string;
  name?: string;
  eventType?: string;
  desc: boolean;
}
// 获取事件列表
export function getDeviceEventeData(data: GetEventQueryParam) {
  return request<PagingRes<Event>>({
    url: '/admin/device/query/device/event/page',
    method: 'post',
    data,
  })
}

// 查询sim卡分页列表
export function querySimList(params: PagingParam & { batchNo: string }) {
  return request<PagingRes<SimAndDevice>>({
    url: '/admin/device/query/device/sim/page',
    method: 'get',
    params,
  })
}

// 查询sim卡总览统计数据
export function getSimOverview(params: { batchNo: string }) {
  return request<SimOverview>({
    url: '/admin/device/get/sim/overview',
    method: 'get',
    params,
  })
}



// 设备管理导出excel
export function handleDeviceExport(data: { id: string, result: string }) {
  return request<string>({
    url: '/admin/device/export/device/by/condition',
    method: 'post',
    data,
  })
}


// 设备管理查询异常设备分页列表
interface QueryAbnprmalDeviceListParam {
  category?: string;
  factory?: string;
  model?: string;
}
export function queryAbnormalDeviceList(data: TypeWithPagingParam<QueryAbnprmalDeviceListParam>) {
  return request<PagingRes<AbnormalDevice>>({
    url: '/admin/device/query/abnormal/device/page',
    method: 'post',
    data,
  })
}

export function exportAbnormalDeviceList(data: QueryAbnprmalDeviceListParam) {
  return request<string>({
    url: '/admin/device/abnormal/device/export',
    method: 'post',
    data,
  })
}



// 导出sim卡验证通知
export function exportSimVerifyNotify() {
  return request<null>({
    url: '/admin/device/export/sim/verify/notify',
    method: 'get',
    noToast: true,
  })
}





